import { Client, cacheExchange, fetchExchange } from '@urql/core';
import { persistedExchange } from '@urql/exchange-persisted';

import { print, concatAST, visit } from 'graphql';

import { EntryReferenceFragment } from '@/components/common/EntryReferenceFragment.gql';
import { LinkFragment } from '@/components/common/LinkFragment.gql';
import { ArticlePreviewFragment } from '@/components/common/ArticlePreviewFragment.gql';
import { StoryPreviewFragment } from '@/components/common/StoryPreviewFragment.gql';
import { RoutinePreviewFragment } from '@/components/common/RoutinePreviewFragment.gql';
import { CraftAssetFragment } from '@/components/common/CraftAssetFragment.gql';

// transform site variable to match craft cms site handle
export const transformSiteVariable = function (variables) {
  if (!variables) {
    return {};
  }

  const map = {
    'ch-fr': 'chFr',
    'ch-de': 'ch',
  };

  if (variables.uri) {
    // check for each mapped locale if the uri contains language prefix
    for (const [key, value] of Object.entries(map)) {
      if (variables.uri.startsWith(`/${key}/`)) {
        variables.uri = variables.uri.replace(`/${key}/`, `/${value}/`);
        break;
      }
    }
  }

  if (variables.site && map[variables.site]) {
    variables.site = map[variables.site];
  }

  return variables;
};

export const sanitizeToken = (token) => {
  if (!token) return undefined;
  if (token.length === 0) return undefined;
  if (token.includes('?')) return undefined;
  return token;
};

export default defineNuxtPlugin((nuxtApp) => {
  const { $config, _route: route } = nuxtApp;

  // Build the GraphQL endpoint URL - must be absolute for SSR
  let url = '/api/content/graphql';

  // On server-side, we need an absolute URL for fetch to work
  if (import.meta.server) {
    const host = process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : 'http://localhost:3000';
    url = `${host}${url}`;
  }

  const token = sanitizeToken(route.query.token);
  const preview = route.query['x-craft-live-preview'];
  const vercelNoCache = route.query._vercel_no_cache === '1';

  let options = {};

  if (token || preview || vercelNoCache) {
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    if (preview) params.set('x-craft-live-preview', preview);
    if (vercelNoCache) params.set('_vercel_no_cache', '1');

    const separator = url.includes('?') ? '&' : '?';
    url += separator + params.toString();

    options = {
      headers: {
        Authorization: `Bearer ${$config.public.cmsAuthToken}`,
      },
    };
  }

  const client = new Client({
    url: url,
    fetchOptions: options,
    exchanges: [
      cacheExchange,
      persistedExchange({
        preferGetForPersistedQueries: 'within-url-limit',
        enforcePersistedQueries: false,
      }),
      fetchExchange,
    ],
  });

  /* Add Fragments here that should be globally available in the Craft requests */
  const commonFragments = [
    ArticlePreviewFragment,
    CraftAssetFragment,
    EntryReferenceFragment,
    LinkFragment,
    RoutinePreviewFragment,
    StoryPreviewFragment,
  ];

  // sanitize gql to graphql-string
  const request = async (query, variables, logQuery = false) => {
    // Skip GraphQL requests in Storybook (no backend available)
    if (typeof window !== 'undefined' && window.__STORYBOOK_PREVIEW__) {
      return undefined;
    }

    if (typeof query !== 'object' && query.kind !== 'Document') {
      throw new Error('Query must be a graphql query object');
    }

    const usedFragments = new Set();
    visit(query, {
      FragmentSpread(node) {
        usedFragments.add(node.name.value);
      },
    });

    const missingFragments = commonFragments.filter((fragment) =>
      usedFragments.has(fragment.definitions[0].name.value)
    );

    if (missingFragments.length) {
      query = concatAST([query, ...missingFragments]);
    }

    if (logQuery) {
      console.log(print(query));
    }

    const queryString = print(query);

    try {
      const response = await client.query(queryString, transformSiteVariable(variables)).toPromise();

      if (response && response.error) {
        const isNetworkError = response.error.message?.includes('[Network]');
        if (!isNetworkError) {
          console.error('GraphQL Response Error:', response.error);
        }
        throw response.error;
      }

      if (response && Array.isArray(response.errors) && response.errors.length > 0) {
        console.error('GraphQL Response Errors:', response.errors);
        const aggregatedError = new Error('GraphQL responded with errors');
        aggregatedError.responseErrors = response.errors;
        throw aggregatedError;
      }

      return response ? response.data : undefined;
    } catch (error) {
      const isNetworkError = error.message?.includes('[Network]');
      if (!isNetworkError) {
        console.error('GraphQL Request Error:', error);
      }
      throw error;
    }
  };

  return {
    provide: {
      craftRequest: request,
    },
  };
});
